import { useState, useCallback, useContext } from "react";
import { dataSyncService, SyncProgress } from "../services/dataSyncService";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { useAppSelector } from "../../app/hooks";
import { AuthContext } from "react-oauth2-code-pkce";

export interface UseDataSyncReturn {
    syncData: () => Promise<void>;
    isLoading: boolean;
    progress: SyncProgress | null;
    lastSyncDate: string;
}

export const useDataSync = (): UseDataSyncReturn => {
    const lastSync = useAppSelector((state) => state.localData.lastSync);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState<SyncProgress | null>(null);

    const authContext = useContext(AuthContext) || null;

    const lastSyncDate = lastSync
        ? format(new Date(lastSync), "d MMM yyyy", { locale: nl })
        : "Never";

    const syncData = useCallback(async () => {
        setIsLoading(true);
        setProgress(null);

        try {
            await dataSyncService.syncAllData((syncProgress) => {
                setProgress(syncProgress);
            });
        } catch (error: unknown) {
            const apiError = error as {
                status?: number;
                data?: { status?: number };
            };
            if (apiError?.status === 401 || apiError?.data?.status === 401) {
                console.log(
                    "Authentication expired during sync, redirecting to login",
                );
                (authContext?.logIn ?? (() => {}))();
                return;
            }

            console.error("Data sync failed:", error);
        } finally {
            setIsLoading(false);
            setProgress(null);
        }
    }, [authContext]);

    return {
        syncData,
        isLoading,
        progress,
        lastSyncDate,
    };
};
